import * as React from 'react'
import {
  CaretSortIcon,
  InfoCircledIcon,
  SymbolIcon,
  TextAlignBottomIcon,
  TextAlignCenterIcon,
  TextAlignJustifyIcon,
  TextAlignLeftIcon,
  TextAlignMiddleIcon,
  TextAlignRightIcon,
  TextAlignTopIcon,
  UploadIcon,
} from '@radix-ui/react-icons'
import { clsx } from 'clsx'
import { useEditorMode } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/editor-mode-provider'
import { Button } from '~/sotano-kit/primitives/button'
import { $button } from '~/sotano-kit/primitives/button/styles'
import { Input, Label } from '~/sotano-kit/primitives/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/sotano-kit/primitives/select'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/sotano-kit/primitives/tooltip'
import { isHotkey } from 'is-hotkey'
import { toast } from 'sonner'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import {
  GoogleFontsItem,
  OgImageBlockImageLayerType,
} from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/og-image/types'
import { useUploadFileToRepoMutation } from '~/hooks-data/use-upload-file-to-repo-mutation'
import { FileLoadingIndicator } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/media/input'
import {
  OG_IMAGE_COMPATIBLE_MIME_TYPES,
  isOgImageCompatibleType,
} from '@basehub/data/replicache/schema/block/valid-mime-types'
import {
  GetVariables,
  VariablesInput,
} from '~/components/variables/single-line-input'

export const CustomTextOption = ({
  name,
  value,
  mutate,
  placeholder,
  tooltipText,
  forceEditable,
  horizontal = false,
  multiLine = false,
  className,
  getVariables,
}: {
  name: string
  value: string
  type?: string
  mutate: (newValue: string) => void
  placeholder?: string
  tooltipText?: string
  forceEditable?: boolean
  inputProps?: React.ComponentProps<typeof Input>
  horizontal?: boolean
  multiLine?: boolean
  className?: string
  getVariables: GetVariables
}) => {
  const id = React.useId()
  const editorMode = useEditorMode()

  const disabled =
    editorMode !== 'write:all' &&
    editorMode !== 'write:value-only' &&
    !forceEditable

  return (
    <Label
      htmlFor={id}
      label={
        <div className="flex justify-between w-full items-baseline min-w-[84px]">
          <span className="items-center flex">
            {name}{' '}
            {tooltipText && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <InfoCircledIcon className="ml-2" />
                </TooltipTrigger>
                <TooltipContent>{tooltipText}</TooltipContent>
              </Tooltip>
            )}
          </span>
        </div>
      }
      size="xs"
      blocked={false}
      className={clsx(className, multiLine && 'flex flex-col')}
      horizontal={horizontal}
      onClick={(e) => {
        e.currentTarget
          .querySelector<HTMLDivElement>('[contenteditable="true"]')
          ?.focus()
      }}
    >
      <VariablesInput
        getVariables={getVariables}
        value={value}
        setValue={mutate}
        placeholder={placeholder}
        disabled={disabled}
        variant={multiLine ? 'textarea' : 'input'}
        id={id}
        disableSyncValueRateLimit
        hideInfo
      />
    </Label>
  )
}

export function HorizontalColorInput({
  label,
  value,
  mutate,
  readOnly,
  className,
  layerId,
}: {
  label: React.ReactNode | string
  value: string
  mutate: (newValue: string) => Promise<string>
  readOnly?: boolean
  className?: string
  layerId: string
}) {
  const id = React.useId()
  const colorPickerInputRef = React.useRef<HTMLInputElement>(null)
  const textInputRef = React.useRef<HTMLInputElement>(null)

  const [internalValue, setInternalValue] = React.useState(value) // internal state for ala Figma input behavior

  React.useEffect(() => {
    setInternalValue(value)
  }, [value])

  return (
    <Label label={label} size="xs" blocked={false} className={className}>
      <div className="relative flex items-center justify-start gap-1.5 h-7 w-full">
        <div
          style={{ backgroundColor: internalValue }}
          className="w-5 h-5 rounded border shrink-0 border-solid border-dark-border-solid z-50 absolute left-1"
        />
        <input
          ref={colorPickerInputRef}
          type="color"
          readOnly={readOnly}
          className="w-6 h-7 opacity-0 absolute left-0"
          defaultValue={value}
          onBlur={() => {
            mutate(internalValue)
          }}
          onInput={(e) => {
            const newValue = e.currentTarget.value
            setInternalValue(newValue)
            const el = document.getElementById(layerId)
            if (!el) return
            const currentColor = el.style.getPropertyValue('color')
            const currentBg = el.style.getPropertyValue('background-color')

            if (currentColor) {
              // has color
              el.style.setProperty('color', newValue)
            }
            if (currentBg) {
              // has bg color
              el.style.setProperty('background-color', newValue)
            }
          }}
        />
        <Input
          ref={textInputRef}
          id={id}
          type="text"
          value={internalValue.toUpperCase()}
          onChange={(e) => {
            setInternalValue(e.target.value)
          }}
          onFocus={(e) => e.target.select()}
          onBlur={(e) =>
            mutate(e.target.value).then((newValue: string) => {
              setInternalValue(newValue)
            })
          }
          onKeyDown={(e) => {
            if (e.code === 'Enter') {
              textInputRef.current?.blur()
            }
          }}
          autoComplete="off"
          size="xs"
          readOnly={readOnly}
          className="w-full absolute !h-7 pl-[1.9375rem] z-0"
        />
      </div>
    </Label>
  )
}

export const basicShortNumberInputRegex = /[^0-9-+.]/g // matches any character that is not a number, minus sign, plus sign, or dot

export function ShortNumberInput({
  label,
  name,
  value,
  mutate,
  horizontal,
  readOnly,
  placeholder,
  inputProps,
}: {
  label: React.ReactNode | string
  name: string
  value: string
  mutate: (newValue: string) => Promise<string>
  horizontal?: boolean
  readOnly?: boolean
  placeholder?: string
  inputProps?: React.ComponentProps<typeof Input>
}) {
  const id = React.useId()
  const editorMode = useEditorMode()
  const disabled = ![
    'write:all',
    'write:value-only',
    'write:value-and-title',
  ].includes(editorMode)

  const inputRef = React.useRef<HTMLInputElement>(null)

  const [internalValue, setInternalValue] = React.useState(value) // internal state for ala Figma input behavior

  React.useEffect(() => {
    setInternalValue(value)
  }, [value])

  return (
    <Label
      htmlFor={id}
      label={
        <div className="flex justify-center w-full items-baseline min-w-[1rem]">
          <span className="items-center flex">{label}</span>
        </div>
      }
      size="xs"
      className={`${horizontal ? '!gap-x-1' : 'flex-col items-start'} flex`}
      horizontal={horizontal}
      blocked={false}
    >
      <Input
        ref={inputRef}
        id={id}
        type="text"
        name={name}
        value={internalValue}
        onFocus={(e) => e.target.select()}
        onChange={(e) => setInternalValue(e.target.value)}
        onBlur={(e) => {
          if (e.target.value !== value) {
            mutate(e.target.value).then((newValue: string) =>
              setInternalValue(newValue)
            )
          }
        }}
        onKeyDown={async (e) => {
          if (isHotkey('enter', e)) {
            inputRef.current?.blur()
          }
          if (isHotkey('ArrowUp', e)) {
            e.preventDefault()
            if (inputRef.current?.value) {
              const formatValue = Number(
                inputRef.current?.value
                  .replace(',', '.')
                  .replace(basicShortNumberInputRegex, '')
              )
              const newValue =
                formatValue || formatValue === 0
                  ? (formatValue + 1).toString()
                  : (Number(value) + 1).toString()

              await mutate(newValue).then((newValue: string) => {
                setInternalValue(newValue)
              })
              inputRef.current?.select()
            }
          }
          if (isHotkey('shift+ArrowUp', e)) {
            e.preventDefault()
            if (inputRef.current?.value) {
              const formatValue = Number(
                inputRef.current?.value
                  .replace(',', '.')
                  .replace(basicShortNumberInputRegex, '')
              )
              const newValue =
                formatValue || formatValue === 0
                  ? (formatValue + 10).toString()
                  : (Number(value) + 10).toString()

              await mutate(newValue).then((newValue: string) => {
                setInternalValue(newValue)
              })
              inputRef.current?.select()
            }
          }
          if (isHotkey('ArrowDown', e)) {
            e.preventDefault()
            if (inputRef.current?.value) {
              const formatValue = Number(
                inputRef.current?.value
                  .replace(',', '.')
                  .replace(basicShortNumberInputRegex, '')
              )

              const newValue =
                formatValue || formatValue === 0
                  ? (formatValue - 1).toString()
                  : (Number(value) - 1).toString()

              await mutate(newValue).then((newValue: string) => {
                setInternalValue(newValue)
              })
              inputRef.current?.select()
            }
          }
          if (isHotkey('shift+ArrowDown', e)) {
            e.preventDefault()
            if (inputRef.current?.value) {
              const formatValue = Number(
                inputRef.current?.value
                  .replace(',', '.')
                  .replace(basicShortNumberInputRegex, '')
              )
              const newValue =
                formatValue || formatValue === 0
                  ? (formatValue - 10).toString()
                  : (Number(value) - 10).toString()

              await mutate(newValue).then((newValue: string) => {
                setInternalValue(newValue)
              })
              inputRef.current?.select()
            }
          }
        }}
        autoComplete="off"
        size="xs"
        placeholder={placeholder}
        readOnly={readOnly}
        className={clsx(disabled && 'cursor-not-allowed')}
        disabled={disabled}
        {...inputProps}
      />
    </Label>
  )
}

export function StaticImageInput({
  layer,
  updateLayer,
}: {
  layer: OgImageBlockImageLayerType
  updateLayer: (
    layer: OgImageBlockImageLayerType,
    options?: {
      disableUndoRedo?: boolean
    }
  ) => void
}) {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache

  const inputRef = React.useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = React.useState(false)
  const [isUploading, setIsUploading] = React.useState(false)
  const repoId = data?.repoId
  const teamId = data?.team.id

  const { mutateAsync, isLoading: isLoadingUploadFile } =
    useUploadFileToRepoMutation()

  const handleChange: React.ChangeEventHandler<HTMLInputElement> =
    React.useCallback(
      (e) => {
        if (!teamId || !repoId) return
        const file = e.target.files?.[0]
        if (!file || !replicache) return

        if (!isOgImageCompatibleType(file.type)) {
          return toast.error('Invalid file type')
        }

        const timeout = setTimeout(() => {
          toast.error(
            `Error uploading image. This may happen if the file is an SVG which is malformed. Contact us and we'll help you out.`
          )
        }, 3000)

        const image = new Image()
        image.onload = async () => {
          clearTimeout(timeout)
          setIsUploading(true)

          try {
            const { uploadURL } = await mutateAsync({
              repoId,
              teamId,
              file,
            })

            updateLayer({
              ...layer,
              imageValue: {
                type: 'static',
                value: {
                  status: 'uploaded',
                  mimeType: file.type,
                  fileName: file.name,
                  fileSize: file.size,
                  width: image.width,
                  height: image.height,
                  url: uploadURL,
                  cloudflareImage: {
                    uploadState: 'will-not-upload',
                    url: null,
                    reason: 'deprecated',
                  },
                  altText: null,
                  lastModified: file.lastModified,
                },
              },
            })
          } catch (error) {
            console.error(error)
            updateLayer({
              ...layer,
              imageValue: {
                type: 'static',
                value: {
                  status: 'idle',
                },
              },
            })
            toast.error('Image could not be uploaded')
          } finally {
            setIsUploading(false)
          }
        }

        image.src = URL.createObjectURL(file)
        inputRef.current?.value && (inputRef.current.value = '')
      },
      [layer, mutateAsync, replicache, repoId, teamId, updateLayer]
    )

  const isLoading = React.useMemo(
    () => isLoadingUploadFile,
    [isLoadingUploadFile]
  )

  return (
    <>
      <div className="relative h-32">
        {layer.imageValue.type === 'static' &&
        layer.imageValue.value.status === 'uploaded' ? (
          <div className="w-full h-full max-w-full overflow-hidden rounded-md border border-dashed border-dark-border-solid bg-dark-shade p-2 flex justify-center items-center">
            <div
              style={{
                aspectRatio:
                  layer.imageValue.value.width / layer.imageValue.value.height,
                width: layer.imageValue.value.width,
                maxWidth: '100%',
              }}
              className="relative"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={layer.imageValue.value.url}
                alt=""
                loading="lazy"
                decoding="async"
                style={{
                  opacity: isLoading ? 0.3 : 1,
                  backgroundImage: `url(data:image/png;base64,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)`,
                  backgroundRepeat: 'repeat',
                  backgroundSize: '16px',
                }}
                className="w-full"
                onDoubleClick={() => {
                  inputRef.current?.click()
                }}
              />
            </div>
            <Input
              disabled={isLoading}
              className="hidden"
              onChange={handleChange}
              accept={OG_IMAGE_COMPATIBLE_MIME_TYPES.join(',')}
              ref={inputRef}
              type="file"
            />
          </div>
        ) : (
          <>
            <Input
              className={clsx(
                // IMPORTANT! z-index: 30 is important to have a seamless UX when dragging files to the editor
                '!h-32 border-dashed text-opacity-0 file:hidden focus:border-solid relative z-30',
                isDragging && '!bg-dark-shade-hover'
              )}
              type="file"
              onChange={handleChange}
              disabled={isLoading}
              onDragEnter={(e) => {
                e.stopPropagation()
                if (!e.dataTransfer.types.includes('Files')) return
                setIsDragging(true)
              }}
              onDragLeave={(e) => {
                e.stopPropagation()
                setIsDragging(false)
              }}
              onDrop={() => setIsDragging(false)}
              accept={OG_IMAGE_COMPATIBLE_MIME_TYPES.join(',')}
              ref={inputRef}
            />
            <p className="pointer-events-none absolute top-1/2 w-full -translate-y-1/2 text-center text-xs leading-4 text-dark-dim z-30 text-balance px-4">
              {layer.imageValue.type === 'static' && isUploading ? (
                <FileLoadingIndicator />
              ) : isDragging ? (
                'Drop to upload'
              ) : (
                <>
                  Choose a static image
                  <span className="text-dark-muted"> instead</span>
                  <UploadIcon width={14} height={14} className="ml-1 inline" />
                </>
              )}
            </p>
          </>
        )}
      </div>
      {layer.imageValue.type === 'static' &&
        layer.imageValue.value.status === 'uploaded' && (
          <Button
            intent="secondary"
            className="text-xs"
            type="button"
            onClick={() => {
              inputRef.current?.click()
            }}
          >
            <SymbolIcon width={16} height={16} className="mr-1" />
            Replace image
          </Button>
        )}
    </>
  )
}

export function ImageFitSelectInput({
  label,
  value,
  mutate,
}: {
  label: React.ReactNode | string
  value: 'contain' | 'cover' | 'fill'
  mutate: (newValue: string) => void
}) {
  return (
    <Label
      label={
        <div className="flex justify-center w-full items-baseline min-w-[1rem]">
          <span className="items-center flex">{label}</span>
        </div>
      }
      size="xs"
      className="!gap-x-1 flex"
      horizontal={true}
      blocked={false}
    >
      <Select
        value={value.toString()}
        onValueChange={(variant: 'contain' | 'cover' | 'fill') => {
          mutate(variant)
        }}
      >
        <SelectTrigger
          noCaret
          unstyled
          className={$button({
            intent: 'secondary',
            className:
              'w-full justify-between !rounded-md !px-2 text-xs font-normal !text-dark-bright',
          })}
        >
          <span className="line-clamp-1 text-left break-all">
            <SelectValue placeholder="Weight" />
          </span>
          <CaretSortIcon
            width={14}
            height={14}
            className="shrink-0 opacity-50"
          />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem key="cover" value={'cover'}>
              Cover
            </SelectItem>
            <SelectItem key="contain" value={'contain'}>
              Contain
            </SelectItem>
            <SelectItem key="fill" value={'fill'}>
              Fill
            </SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
    </Label>
  )
}

export function FontComboboxInput({
  label,
  value,
  mutate,
  availableFonts,
}: {
  label: string
  value: string // font family
  mutate: (newValue: string) => void
  availableFonts: GoogleFontsItem[]
  popoverContentWidth?: number
  className?: string
}) {
  return (
    <Label label={label} size="xs" blocked={false}>
      <Select
        value={value.toString()}
        onValueChange={(variant: string) => {
          mutate(variant.toLowerCase())
        }}
      >
        <SelectTrigger
          noCaret
          unstyled
          className={$button({
            intent: 'secondary',
            className:
              'w-full justify-between !rounded-md !px-2 text-xs font-normal !text-dark-bright',
          })}
        >
          <span className="line-clamp-1 text-left break-all">
            <SelectValue placeholder="Font" />
          </span>
          <CaretSortIcon
            width={14}
            height={14}
            className="shrink-0 opacity-50"
          />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {availableFonts?.map((font) => (
              <SelectItem
                key={font.family}
                value={font.family}
                style={{
                  contentVisibility: 'auto',
                }}
              >
                {font.family}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </Label>
  )
}

export function FontWeightSelectInput({
  label,
  value,
  mutate,
  fontFamily,
  availableFonts,
}: {
  label: string
  value: number // font weight
  mutate: (newValue: string) => void
  fontFamily: string
  availableFonts: GoogleFontsItem[]
}) {
  return (
    <Label label={label} size="xs" blocked={false}>
      <Select
        value={value.toString()}
        onValueChange={(variant: string) => {
          mutate(variant)
        }}
      >
        <SelectTrigger
          noCaret
          unstyled
          className={$button({
            intent: 'secondary',
            className:
              'w-full justify-between !rounded-md !px-2 text-xs font-normal !text-dark-bright',
          })}
        >
          <span className="line-clamp-1 text-left break-all">
            <SelectValue placeholder="Weight" />
          </span>
          <CaretSortIcon
            width={14}
            height={14}
            className="shrink-0 opacity-50"
          />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {availableFonts
              ?.find((font) => font.family === fontFamily)
              ?.variants.filter(
                (variant: string) => !variant.includes('italic')
              )
              .map((variant: string) => (
                <SelectItem
                  key={variant}
                  value={variant === 'regular' ? '400' : variant}
                  className={
                    variant === '100'
                      ? 'font-thin'
                      : variant === '200'
                        ? 'font-extralight'
                        : variant === '300'
                          ? 'font-light'
                          : variant === 'regular' || variant === '400'
                            ? 'font-normal'
                            : variant === '500'
                              ? 'font-medium'
                              : variant === '600'
                                ? 'font-semibold'
                                : variant === '700'
                                  ? 'font-bold'
                                  : variant === '800'
                                    ? 'font-extrabold'
                                    : variant === '900'
                                      ? 'font-black'
                                      : ''
                  }
                >
                  {variant === '100'
                    ? 'Thin'
                    : variant === '200'
                      ? 'Extra Light'
                      : variant === '300'
                        ? 'Light'
                        : variant === 'regular' || variant === '400'
                          ? 'Regular'
                          : variant === '500'
                            ? 'Medium'
                            : variant === '600'
                              ? 'Semi Bold'
                              : variant === '700'
                                ? 'Bold'
                                : variant === '800'
                                  ? 'Extra Bold'
                                  : variant === '900'
                                    ? 'Black'
                                    : ''}
                </SelectItem>
              ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </Label>
  )
}

export function TextHorizontalAlignSelector({
  value,
  mutate,
}: {
  value: string
  mutate: (newValue: string) => void
}) {
  return (
    <div className="flex gap-1 w-full justify-start items-center">
      <div
        className={`${
          value === 'left' ? 'bg-dark-border-solid' : ''
        } flex justify-center items-center h-6 w-6 rounded shrink-0`}
        onClick={() => mutate('left')}
      >
        <TextAlignLeftIcon
          className="w-4 h-4 shrink-0"
          color={value === 'left' ? '#F3F3F3' : '#909090'}
        />
      </div>
      <div
        className={`${
          value === 'center' ? 'bg-dark-border-solid' : ''
        } flex justify-center items-center h-6 w-6 rounded shrink-0`}
        onClick={() => mutate('center')}
      >
        <TextAlignCenterIcon
          className="w-4 h-4 shrink-0"
          color={value === 'center' ? '#F3F3F3' : '#909090'}
        />
      </div>
      <div
        className={`${
          value === 'right' ? 'bg-dark-border-solid' : ''
        } flex justify-center items-center h-6 w-6 rounded shrink-0`}
        onClick={() => mutate('right')}
      >
        <TextAlignRightIcon
          className="w-4 h-4 shrink-0"
          color={value === 'right' ? '#F3F3F3' : '#909090'}
        />
      </div>
      <div
        className={`${
          value === 'justify' ? 'bg-dark-border-solid' : ''
        } flex justify-center items-center h-6 w-6 rounded shrink-0`}
        onClick={() => mutate('justify')}
      >
        <TextAlignJustifyIcon
          className="w-4 h-4 shrink-0"
          color={value === 'justify' ? '#F3F3F3' : '#909090'}
        />
      </div>
    </div>
  )
}

export function TextVerticalAlignSelector({
  value,
  mutate,
}: {
  value: string
  mutate: (newValue: string) => void
}) {
  return (
    <div className="flex gap-1 w-full justify-end items-center">
      <div
        className={`${
          value === 'top' ? 'bg-dark-border-solid' : ''
        } flex justify-center items-center h-6 w-6 rounded shrink-0`}
        onClick={() => mutate('top')}
      >
        <TextAlignTopIcon
          className="w-4 h-4 shrink-0"
          color={value === 'top' ? '#F3F3F3' : '#909090'}
        />
      </div>
      <div
        className={`${
          value === 'middle' ? 'bg-dark-border-solid' : ''
        } flex justify-center items-center h-6 w-6 rounded shrink-0`}
        onClick={() => mutate('middle')}
      >
        <TextAlignMiddleIcon
          className="w-4 h-4 shrink-0"
          color={value === 'middle' ? '#F3F3F3' : '#909090'}
        />
      </div>
      <div
        className={`${
          value === 'bottom' ? 'bg-dark-border-solid' : ''
        } flex justify-center items-center h-6 w-6 rounded shrink-0`}
        onClick={() => mutate('bottom')}
      >
        <TextAlignBottomIcon
          className="w-4 h-4 shrink-0"
          color={value === 'bottom' ? '#F3F3F3' : '#909090'}
        />
      </div>
    </div>
  )
}
